import { useQuery } from "@apollo/client";
import { clientNeoSociete } from "@neonumy/api-clients";
import { AutoComplete, Form } from "antd";
import { LIST_COMPANIES_CUSTOM } from "queries/neosociete/companies";

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "use-debounce";
import { generateIntracomVatNumber } from "utils/vatUtils";

interface Company {
  name: string;
  vatNumber: string;
  headquartersNic?: string;
  establishments?: Establishment[];
}

interface Establishment {
  isHeadquarter: boolean;
  township: string;
  address: string;
  addressNr?: string;
  address2?: string;
  postalCode: string;
}

interface AutoCompleteProps {
  placeholder?: string;
  variablesExtractor?: (searchTerm: string) => Record<string, unknown>;
  isVat?: boolean;
  disabled?: boolean;
  [key: string]: unknown;
}

const DEBOUNCE_TIMEOUT = 500;

export default function CompaniesAutocomplete(props: AutoCompleteProps): React.ReactElement {
  const { placeholder, variablesExtractor, isVat, disabled, ...rest } = props;
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");

  const form = Form.useFormInstance();

  const [value] = useDebounce(searchTerm, DEBOUNCE_TIMEOUT);

  const { loading, error, data } = useQuery<ListCompaniesSocieteResponse, ListCompaniesSocieteVars>(
    LIST_COMPANIES_CUSTOM,
    {
      client: clientNeoSociete,
      variables: variablesExtractor!(value),
      skip: value.length < 3,
    },
  );

  const getAutoCompleteOptions = () => {
    if (value.length < 3) {
      return [];
    }

    if (loading) {
      return [{ value: t("Loading...") }];
    }

    if (error) {
      return [{ value: t("Error loading options") }];
    }

    return (
      data?.listCompanies.results.map((item: Company) => {
        const establishment: Establishment | undefined =
          (item?.establishments?.length ?? 0) > 0
            ? item?.establishments?.find(
                (establishment: Establishment) => establishment?.isHeadquarter === true,
              )
            : undefined;

        const label = isVat ? (
          <div key={item.vatNumber}>
            <span>
              {item.vatNumber} {item?.headquartersNic}
            </span>{" "}
            <span className="custom-label">{`- ${establishment?.township}`}</span>
          </div>
        ) : (
          <div key={item.name}>
            <span>{item?.name}</span>{" "}
            <span className="custom-label">
              {`${item?.vatNumber} - ${establishment?.township}`}
            </span>
            <div className="custom-address-label">
              {`${establishment?.address} ${establishment?.postalCode}`}
            </div>
          </div>
        );

        return {
          label,
          value: isVat ? item.vatNumber : item.name,
          data: item,
        };
      }) || []
    );
  };

  const onSearch = (searchText: string) => {
    setSearchTerm(searchText);
  };

  const onSelect = (selectedValue: string) => {
    const selectedCompanyData = data?.listCompanies?.results.find(
      (company: Company) => company?.name === selectedValue || company?.vatNumber === selectedValue,
    );
    const headquarter = selectedCompanyData?.establishments?.find(
      (establishment: Establishment) => establishment?.isHeadquarter === true,
    );

    const intracomVatNumber = selectedCompanyData?.vatNumber
      ? generateIntracomVatNumber(selectedCompanyData.vatNumber)
      : "";

    form.setFieldsValue({
      name: selectedCompanyData?.name,
      vatNumber:
        `${selectedCompanyData?.vatNumber ?? ""} ${selectedCompanyData?.headquartersNic ?? ""}`.trim(),
      headquartersNic: selectedCompanyData?.headquartersNic,
      euVatNumber: intracomVatNumber,
      companyFromSelect: selectedCompanyData,
      addressNr: headquarter?.addressNr || "",
      streetName: headquarter?.address || "",
      streetNameComplement: headquarter?.address2 || "",
      postalCode: {
        label: `${headquarter?.postalCode} - ${headquarter?.township}`,
        value: `${headquarter?.postalCode}`,
      },
      city: {
        label: headquarter?.township,
        value: headquarter?.township,
      },
      country: {
        label: "France",
        value: "FR",
      },
    });
  };

  return (
    <AutoComplete
      options={getAutoCompleteOptions()}
      onSelect={onSelect}
      onSearch={onSearch}
      placeholder={placeholder}
      disabled={disabled}
      allowClear={true}
      dropdownMatchSelectWidth={false}
      onClear={() => {
        form.resetFields();
      }}
      {...rest}
    />
  );
}
